import type { FunctionalComponent, PropType } from 'vue'
import { debounce } from 'lodash-es'
import Suggest from './suggest.vue'
import { getTagIDs } from './info'
import type { TagIds, Template } from '~/api/interface'
import MoreIcon from '~/assets/svg/arrowoutlin.svg?url'
import { getSimilarList, getSimilarListByUser } from '~/api'
import { SCROLL_LIST } from '~/hooks/useScrollList'
import Resource from '~/components/resource'
import { PageKey } from '~/constants/page'

const getTagIDsJoinUnderline = (ids?: TagIds, level = 0, pid = '') => {
  if (!ids) {
    return
  }

  let result = ''
  for (const key in ids) {
    const value = ids[key]
    const parentId = `${level},${key}`
    if (Array.isArray(value)) {
      value.forEach((v) => {
        result += `${pid ? pid + ':' : ''}${parentId}:${level + 1},${v}_`
      })
    } else if (typeof value === 'object') {
      result += getTagIDsJoinUnderline(value, level + 1, parentId) + '_'
    }
  }
  return result.slice(0, -1)
}

// const toMore = (tag_ids?: string) => {

// }

export const SimilarItem: FunctionalComponent<{
  data: Template
  onClick?: (data: Template) => void
}> = (props) => {
  return (
    <div class="flex-1 rounded-[10px] border-[1px] border-[#e9e9e9] border-solid overflow-hidden cursor-pointer transition-transform duration-300 ease hover:-translate-y-[6px]">
      <Resource
        src={props.data?.cover_url}
        transformResource={transformImage}
        class="w-full h-[122px] object-cover block"
      />
      <span class="block p-[14px] flex-y-center text-14 tracking-[1px] font-semibold truncate">
        <a-tooltip
          placement="topLeft"
          destroyTooltipOnHide
          overlayClassName="card-tip"
          color="white"
        >
          {{
            default: () => props.data?.name,
            title: () => props.data?.name,
          }}
        </a-tooltip>
      </span>
    </div>
  )
}

export const Similar = defineComponent({
  name: 'Similar',
  props: {
    title: {
      type: String,
      default: '',
    },
    cardHeight: {
      type: Number,
      default: 188,
    },
    active: {
      type: Object as PropType<Template | null>,
      default: () => ({}),
      require: false,
    },
    isBestPractice: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['similarClick'],
  setup(props, { emit }) {
    const suggestRef = ref()
    const { $t } = useNuxtApp()

    const resourceLocation = injectResourceLocation()

    const getSimilarData = async (params: any) =>
      await getSimilarList({
        ...params,
        template_id: props.active?.id,
        ...(props.isBestPractice && {
          is_personal_excellent_template: 1,
        }),
      })

    const getSimilarDataByUser = async (params: any) =>
      await getSimilarListByUser({
        ...params,
        many: 1,
        // is_personal_excellent_template: 1,
      })

    const onItemClick = (data: Template, list: Template[]) => {
      emit('similarClick', list, data)
    }

    watch(
      () => props.active,
      debounce(() => {
        suggestRef.value?.reload()
      }, 500)
    )

    const router = useRouter()

    function toMore(tag_ids?: string) {
      trackEvent(TRACK_EVENT.CLICK_TEMPLATE_MORE, {
        source: resourceLocation?.source,
        template_id: props.active?.id,
        template_title: props.active?.name,
        tags: getTagIDs(props.active?.tag_ids)
          .map((item) => item.tag_name)
          .join(','),
      })

      const open = useTemplateVisible()
      open.value = false

      router.push({ name: PageKey.TEMPLATE, query: { tag_ids } })
    }

    return () => (
      <>
        <div class="flex-between mb-[5px]">
          <span class="text-[16px] leading-[1] text-font-1 font-bold">
            {props.title}
          </span>
          {isGlobalVersion() ? null : (
            <a-button
              type="text"
              class="more-btn h-[16px]"
              onClick={() =>
                toMore(getTagIDsJoinUnderline(props.active?.tag_ids))
              }
            >
              {$t('cha-kan-geng-duo')}
              <Resource src={MoreIcon} class="w-[16px] h-[16px] opacity-30" />
            </a-button>
          )}
        </div>
        <Suggest
          ref={suggestRef}
          height={45}
          id={SCROLL_LIST.SIMILAR_TEMPLATE}
          api={!props.isBestPractice ? getSimilarData : getSimilarDataByUser}
          collect={false}
          share={false}
          itemHeight={props.cardHeight}
          filterId={props.active?.id}
          small={true}
          itemNumber={4}
          templateSource={TEMPLATE_SOURCE.SIMILAR_RECOM}
          // @ts-ignore
          onItemClick={onItemClick}
          isBestPractice={props.isBestPractice}
        />
      </>
    )
  },
})
